import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle, Pencil, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';

const inputClass = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400';
const labelClass = 'block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5';

const ProfilePage = () => {
  const { currentUser, userData, updateUserData } = useAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phone: '',
    specialty: 'general',
    company: '',
    description: '',
    location: '',
    hourlyRate: '',
    experience: '',
    certifications: '',
    availability: 'available',
    address: '',
  });

  useEffect(() => {
    if (userData) {
      setProfileData({
        displayName: userData.displayName || '',
        email: userData.email || currentUser?.email || '',
        phone: userData.phone || '',
        specialty: userData.specialty || 'general',
        company: userData.company || '',
        description: userData.description || '',
        location: userData.location || '',
        hourlyRate: userData.hourlyRate || '',
        experience: userData.experience || '',
        certifications: userData.certifications || '',
        availability: userData.availability || 'available',
        address: userData.address || '',
      });
    }
  }, [userData, currentUser?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await updateUserData(profileData);
      setMessage('Profile updated successfully.');
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Error updating profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!userData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <Link to="/dashboard" className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 shrink-0">
                  <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
                </Link>
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-slate-800">Profile Management</h1>
                  <p className="text-xs text-slate-500">Update your account and business details</p>
                </div>
              </div>
            </div>
          </header>

          <div className="p-6 max-w-3xl">
            {message && (
              <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-slate-100 text-slate-800'}`}>
                {message}
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                <h2 className="text-sm font-semibold text-slate-800">Edit profile</h2>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Full name</label>
                    <input type="text" required value={profileData.displayName} onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })} className={inputClass} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input type="email" required value={profileData.email} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} className={inputClass} placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input type="tel" value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} className={inputClass} placeholder="(555) 123-4567" />
                  </div>
                  {(userData.role === 'owner' || userData.role === 'tenant') && (
                    <div className="md:col-span-2">
                      <label className={labelClass}>Address</label>
                      <input type="text" value={profileData.address} onChange={(e) => setProfileData({ ...profileData, address: e.target.value })} className={inputClass} placeholder="Street, city, state" />
                    </div>
                  )}
                </div>

                {userData.role === 'serviceProvider' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                      <div>
                        <label className={labelClass}>Specialty</label>
                        <select value={profileData.specialty} onChange={(e) => setProfileData({ ...profileData, specialty: e.target.value })} className={inputClass}>
                          <option value="general">General</option>
                          <option value="plumbing">Plumbing</option>
                          <option value="electrical">Electrical</option>
                          <option value="hvac">HVAC</option>
                          <option value="carpentry">Carpentry</option>
                          <option value="painting">Painting</option>
                          <option value="cleaning">Cleaning</option>
                          <option value="landscaping">Landscaping</option>
                          <option value="roofing">Roofing</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Company</label>
                        <input type="text" value={profileData.company} onChange={(e) => setProfileData({ ...profileData, company: e.target.value })} className={inputClass} placeholder="Company name" />
                      </div>
                      <div>
                        <label className={labelClass}>Location</label>
                        <input type="text" value={profileData.location} onChange={(e) => setProfileData({ ...profileData, location: e.target.value })} className={inputClass} placeholder="City, State" />
                      </div>
                      <div>
                        <label className={labelClass}>Hourly rate ($)</label>
                        <input type="number" step="0.01" value={profileData.hourlyRate} onChange={(e) => setProfileData({ ...profileData, hourlyRate: e.target.value })} className={inputClass} placeholder="25.00" />
                      </div>
                      <div>
                        <label className={labelClass}>Availability</label>
                        <select value={profileData.availability} onChange={(e) => setProfileData({ ...profileData, availability: e.target.value })} className={inputClass}>
                          <option value="available">Available</option>
                          <option value="busy">Busy</option>
                          <option value="unavailable">Unavailable</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Experience (years)</label>
                        <input type="number" value={profileData.experience} onChange={(e) => setProfileData({ ...profileData, experience: e.target.value })} className={inputClass} placeholder="5" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Certifications</label>
                      <input type="text" value={profileData.certifications} onChange={(e) => setProfileData({ ...profileData, certifications: e.target.value })} className={inputClass} placeholder="Licensed Plumber, HVAC Certified, etc." />
                    </div>
                    <div>
                      <label className={labelClass}>Description</label>
                      <textarea rows={4} value={profileData.description} onChange={(e) => setProfileData({ ...profileData, description: e.target.value })} className={inputClass} placeholder="Tell clients about your services and experience..." />
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                  <Link to="/dashboard" className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                    Cancel
                  </Link>
                  <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors">
                    {saving ? 'Saving…' : 'Save profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
